/**
 * Smart Variant Selector
 * Handles variant selection with unavailable states, notify me, and URL sync
 */

import type { ShopifyProduct, ShopifyVariant } from '../../types/shopify';
import type { VariantSelectorSettings } from '../../types/settings';
import { SettingsParser } from '../../types/settings';
import { eventBus } from '../../types/events';
import { qs, qsa, addClass, removeClass } from '../../utils/dom';
import { NotifyMeStorage } from '../../utils/storage';

export class VariantSelector {
  private product: ShopifyProduct;
  private settings: SettingsParser<VariantSelectorSettings>;
  private currentVariant: ShopifyVariant;
  private selectedOptions: Map<string, string> = new Map();

  constructor(
    private container: HTMLElement,
    productData: ShopifyProduct
  ) {
    this.product = productData;
    this.settings = new SettingsParser(container);
    this.currentVariant = this.product.variants[0];

    this.init();
  }

  private init(): void {
    // Parse URL for variant parameter
    if (this.settings.get('enable_url_sync', true)) {
      this.syncFromURL();
    }

    // Initialize option selectors
    this.initializeOptions();

    // Setup change handlers
    this.setupEventListeners();

    // Initial render
    this.render();
  }

  private syncFromURL(): void {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');

    if (variantId) {
      const variant = this.product.variants.find(
        (v) => v.id === parseInt(variantId, 10)
      );

      if (variant) {
        this.currentVariant = variant;
        this.updateSelectedOptions(variant);
      }
    }
  }

  private updateSelectedOptions(variant: ShopifyVariant): void {
    if (variant.option1) this.selectedOptions.set(this.product.options[0].name, variant.option1);
    if (variant.option2) this.selectedOptions.set(this.product.options[1].name, variant.option2);
    if (variant.option3) this.selectedOptions.set(this.product.options[2].name, variant.option3);
  }

  private initializeOptions(): void {
    this.product.options.forEach((option, index) => {
      const selector = qs<HTMLSelectElement>(
        `[data-option-selector="${index}"]`,
        this.container
      );

      if (selector) {
        // Set initial value
        const currentValue =
          index === 0
            ? this.currentVariant.option1
            : index === 1
            ? this.currentVariant.option2
            : this.currentVariant.option3;

        if (currentValue) {
          selector.value = currentValue;
          this.selectedOptions.set(option.name, currentValue);
        }
      }
    });
  }

  private setupEventListeners(): void {
    // Option selectors
    const selectors = qsa<HTMLSelectElement>(
      '[data-option-selector]',
      this.container
    );

    selectors.forEach((selector) => {
      selector.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const optionIndex = parseInt(target.dataset.optionSelector || '0', 10);
        const optionName = this.product.options[optionIndex].name;

        this.selectedOptions.set(optionName, target.value);
        this.handleOptionChange();
      });
    });

    // Notify me button
    const notifyBtn = qs('[data-notify-trigger]', this.container);
    if (notifyBtn) {
      notifyBtn.addEventListener('click', () => this.openNotifyModal());
    }
  }

  private handleOptionChange(): void {
    // Find variant matching selected options
    const variant = this.findVariantByOptions();

    if (variant) {
      this.currentVariant = variant;

      // Update URL
      if (this.settings.get('enable_url_sync', true)) {
        this.updateURL(variant.id);
      }

      // Update UI
      this.render();

      // Dispatch event
      eventBus.dispatch('variant:change', {
        variant,
        product: this.product,
      });

      // Handle unavailable state
      if (!variant.available) {
        eventBus.dispatch('variant:unavailable', {
          variant,
          product: this.product,
        });
      }
    }

    // Update option availability
    this.updateOptionAvailability();
  }

  private findVariantByOptions(): ShopifyVariant | undefined {
    return this.product.variants.find((variant) => {
      const option1Match =
        !this.product.options[0] ||
        variant.option1 === this.selectedOptions.get(this.product.options[0].name);
      const option2Match =
        !this.product.options[1] ||
        variant.option2 === this.selectedOptions.get(this.product.options[1].name);
      const option3Match =
        !this.product.options[2] ||
        variant.option3 === this.selectedOptions.get(this.product.options[2].name);

      return option1Match && option2Match && option3Match;
    });
  }

  private updateOptionAvailability(): void {
    this.product.options.forEach((option, optionIndex) => {
      const selector = qs<HTMLSelectElement>(
        `[data-option-selector="${optionIndex}"]`,
        this.container
      );

      if (!selector) return;

      option.values.forEach((value) => {
        // Check if this value combination is available
        const testOptions = new Map(this.selectedOptions);
        testOptions.set(option.name, value);

        const hasAvailableVariant = this.product.variants.some((variant) => {
          const matches = this.variantMatchesOptions(variant, testOptions);
          return matches && variant.available;
        });

        // Update option element
        const optionEl = selector.querySelector<HTMLOptionElement>(
          `option[value="${value}"]`
        );

        if (optionEl) {
          optionEl.disabled = !hasAvailableVariant;
          if (!hasAvailableVariant) {
            optionEl.textContent = `${value} - Unavailable`;
          }
        }
      });
    });
  }

  private variantMatchesOptions(
    variant: ShopifyVariant,
    options: Map<string, string>
  ): boolean {
    for (let i = 0; i < this.product.options.length; i++) {
      const optionName = this.product.options[i].name;
      const selectedValue = options.get(optionName);
      const variantValue =
        i === 0 ? variant.option1 : i === 1 ? variant.option2 : variant.option3;

      if (selectedValue && variantValue !== selectedValue) {
        return false;
      }
    }
    return true;
  }

  private updateURL(variantId: number): void {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId.toString());
    window.history.replaceState({}, '', url.toString());
  }

  private render(): void {
    // Update price
    const priceEl = qs('[data-variant-price]', this.container);
    if (priceEl) {
      const price = (this.currentVariant.price / 100).toFixed(2);
      priceEl.textContent = `$${price}`;
    }

    // Update compare at price
    const compareEl = qs('[data-variant-compare-price]', this.container);
    if (compareEl && this.currentVariant.compare_at_price) {
      const comparePrice = (this.currentVariant.compare_at_price / 100).toFixed(2);
      compareEl.textContent = `$${comparePrice}`;
      removeClass(compareEl, 'hidden');
    } else if (compareEl) {
      addClass(compareEl, 'hidden');
    }

    // Update variant ID input
    const idInput = qs<HTMLInputElement>('[name="id"]', this.container);
    if (idInput) {
      idInput.value = this.currentVariant.id.toString();
    }

    // Update availability UI
    this.updateAvailabilityUI();

    // Update inventory count
    if (this.settings.get('show_inventory_count', false)) {
      this.updateInventoryUI();
    }

    // Update featured image
    if (this.currentVariant.featured_image) {
      const imgEl = qs<HTMLImageElement>('[data-variant-image]', this.container);
      if (imgEl) {
        imgEl.src = this.currentVariant.featured_image.src;
      }
    }
  }

  private updateAvailabilityUI(): void {
    const addToCartBtn = qs<HTMLButtonElement>(
      '[data-add-to-cart]',
      this.container
    );
    const notifyBtn = qs('[data-notify-trigger]', this.container);
    const availabilityText = qs('[data-availability-text]', this.container);

    if (!this.currentVariant.available) {
      // Sold out state
      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Sold Out';
      }

      if (
        notifyBtn &&
        this.settings.get('enable_notify_me', true)
      ) {
        removeClass(notifyBtn, 'hidden');
      }

      if (availabilityText) {
        availabilityText.textContent = 'Out of stock';
        addClass(availabilityText, 'text-error');
      }
    } else {
      // Available state
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'Add to Cart';
      }

      if (notifyBtn) {
        addClass(notifyBtn, 'hidden');
      }

      if (availabilityText) {
        availabilityText.textContent = 'In stock';
        removeClass(availabilityText, 'text-error');
        addClass(availabilityText, 'text-success');
      }
    }
  }

  private updateInventoryUI(): void {
    const inventoryEl = qs('[data-inventory-count]', this.container);
    if (!inventoryEl) return;

    const quantity = this.currentVariant.inventory_quantity || 0;
    const threshold = this.settings.get('low_inventory_threshold', 5);

    if (quantity > 0 && quantity <= threshold) {
      inventoryEl.textContent = `Only ${quantity} left!`;
      removeClass(inventoryEl, 'hidden');
      addClass(inventoryEl, 'text-warning');
    } else {
      addClass(inventoryEl, 'hidden');
    }
  }

  private openNotifyModal(): void {
    const modal = qs('[data-notify-modal]');
    if (!modal) return;

    // Populate modal with variant info
    const titleEl = qs('[data-notify-product-title]', modal);
    const variantEl = qs('[data-notify-variant-title]', modal);

    if (titleEl) titleEl.textContent = this.product.title;
    if (variantEl) variantEl.textContent = this.currentVariant.title;

    // Show modal
    removeClass(modal, 'hidden');
    addClass(modal, 'active');

    // Setup submit handler
    const form = qs<HTMLFormElement>('[data-notify-form]', modal);
    if (form) {
      form.onsubmit = (e) => this.handleNotifySubmit(e);
    }
  }

  private async handleNotifySubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const emailInput = form.querySelector<HTMLInputElement>('[name="email"]');

    if (!emailInput || !emailInput.value) return;

    const email = emailInput.value;

    try {
      // Store locally
      NotifyMeStorage.add({
        email,
        variantId: this.currentVariant.id,
        productTitle: this.product.title,
        variantTitle: this.currentVariant.title,
        timestamp: new Date().toISOString(),
      });

      // Call endpoint if configured
      const endpoint = this.settings.get('notify_endpoint');
      if (endpoint) {
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            variant_id: this.currentVariant.id,
            product_id: this.product.id,
          }),
        });
      }

      // Dispatch success event
      eventBus.dispatch('notify:success', {
        email,
        variantId: this.currentVariant.id,
      });

      // Close modal
      this.closeNotifyModal();

      // Show success message
      this.showNotifySuccess();
    } catch (error) {
      eventBus.dispatch('notify:error', {
        message: error instanceof Error ? error.message : 'Failed to submit',
      });
    }
  }

  private closeNotifyModal(): void {
    const modal = qs('[data-notify-modal]');
    if (modal) {
      addClass(modal, 'hidden');
      removeClass(modal, 'active');
    }
  }

  private showNotifySuccess(): void {
    const successEl = qs('[data-notify-success]', this.container);
    if (successEl) {
      removeClass(successEl, 'hidden');
      setTimeout(() => addClass(successEl, 'hidden'), 5000);
    }
  }

  /**
   * Public API: Get current variant
   */
  getCurrentVariant(): ShopifyVariant {
    return this.currentVariant;
  }

  /**
   * Public API: Set variant programmatically
   */
  setVariant(variantId: number): void {
    const variant = this.product.variants.find((v) => v.id === variantId);
    if (variant) {
      this.currentVariant = variant;
      this.updateSelectedOptions(variant);
      this.initializeOptions();
      this.render();
    }
  }
}

// Auto-initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const containers = qsa<HTMLElement>('[data-variant-selector]');

  containers.forEach((container) => {
    const productData = container.dataset.product;
    if (productData) {
      try {
        const product: ShopifyProduct = JSON.parse(productData);
        new VariantSelector(container, product);
      } catch (error) {
        console.error('Failed to initialize variant selector:', error);
      }
    }
  });
});
